import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FiDownload } from "react-icons/fi";
import { RxCross2 } from "react-icons/rx";
import { FileTypeTile, attachmentDetails, extensionOf } from "./ChatFileCard";
import { APP_PATH } from "../../api/backendPath";
import styles from "./AttachmentPreview.module.scss";

/**
 * In-page preview popup for a chat attachment, so clicking a file no longer opens a new tab.
 * Same design as the admin panel's chat viewer: a white card over a dimmed page with a header
 * (type tile, file name, "PDF · 236 KB", Download, Close) and the file below it.
 *   image          → fitted to the screen
 *   PDF            → browser PDF viewer in an iframe
 *   CSV / TXT      → fetched and shown as a table / plain text (needs the file host to allow
 *                    the request; otherwise it shows a message and Download)
 *   anything else  → (Word, Excel, …) browsers can't render these: a message and Download
 *
 * Closes with Esc, the close button or a click outside the card. Rendered in a portal on
 * <body> so a chat panel's fixed position and overflow can't clip it.
 *
 * Props: url, fileName, kind ("image" | "file"), contentType, fileSize (bytes), onClose
 */

const MAX_TABLE_ROWS = 500;

/** Which viewer to use, from the message type, the MIME type and the file extension. */
const previewTypeOf = ({ kind, contentType = "", fileName = "", url = "" }) => {
  const mime = String(contentType || "").toLowerCase();
  const ext = extensionOf(fileName) || extensionOf(url);
  if (kind === "image" || mime.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) {
    return "image";
  }
  if (mime === "application/pdf" || ext === "pdf") return "pdf";
  if (mime === "text/csv" || ext === "csv") return "csv";
  if (mime === "text/plain" || ext === "txt") return "text";
  return "other";
};

/** Minimal CSV parser: quoted fields, escaped quotes ("") and commas / newlines inside quotes. */
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  const src = text.replace(/^\uFEFF/, "");
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
};

/**
 * Save the file under its name, without leaving the page. Presigned S3 links are cross-origin
 * and the bucket's CORS doesn't cover every origin (local runs), so the browser can't read them
 * itself: the API gateway (POST /consultation/chat/download) returns the bytes and they are saved
 * from a blob link. A local copy (file being sent) is saved directly.
 * Resolves false when the file could not be downloaded.
 */
const downloadFile = async (url, name) => {
  if (!url) return false;
  let href = url;
  let blobUrl = null;
  if (!url.startsWith("blob:")) {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}${APP_PATH.consultationChatDownload}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionStorage.getItem("usr_1a2b3c")}`,
        },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      blobUrl = URL.createObjectURL(await res.blob());
      href = blobUrl;
    } catch (e) {
      return false;
    }
  }
  const link = document.createElement("a");
  link.href = href;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  if (blobUrl) setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  return true;
};

const AttachmentPreview = ({ url, fileName, kind, contentType, fileSize, onClose }) => {
  const { t, i18n } = useTranslation();
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const closeRef = useRef(null);
  // Latest onClose without re-running the open/close effect (callers pass inline arrows, and
  // re-running it on every chat re-render would keep pulling focus back to the close button).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const type = previewTypeOf({ kind, contentType, fileName, url });
  const isImage = type === "image";
  const name = fileName || t("chat.preview.attachment", "Attachment");
  const details = attachmentDetails({ fileName: name, contentType, fileSize, isImage });
  const [loadFailed, setLoadFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);

  useEffect(() => {
    setLoadFailed(false);
    setDownloadFailed(false);
  }, [url]);

  // Text-based files are fetched so they can be shown inline.
  const [textState, setTextState] = useState({ loading: false, error: false, content: null });
  useEffect(() => {
    if (type !== "csv" && type !== "text") return undefined;
    let cancelled = false;
    setTextState({ loading: true, error: false, content: null });
    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) setTextState({ loading: false, error: false, content: text });
      })
      .catch(() => {
        if (!cancelled) setTextState({ loading: false, error: true, content: null });
      });
    return () => {
      cancelled = true;
    };
  }, [type, url]);

  // Esc closes; focus moves to the close button on open and back to the attachment on close;
  // the page behind does not scroll while the popup is open.
  useEffect(() => {
    const previousFocus = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current?.();
    };
    document.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      if (previousFocus && typeof previousFocus.focus === "function") previousFocus.focus();
    };
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadFailed(false);
    try {
      setDownloadFailed(!(await downloadFile(url, name)));
    } finally {
      setDownloading(false);
    }
  };

  const message = (title, hint) => (
    <div className={styles.message}>
      <p className={styles.messageTitle}>{title}</p>
      {hint && <p className={styles.messageHint}>{hint}</p>}
    </div>
  );

  let body;
  if (!url || loadFailed) {
    body = message(
      t("chat.preview.loadFailed", "This file couldn't be loaded for preview. You can download it instead."),
    );
  } else if (type === "image") {
    body = (
      <div className={styles.imageWrap}>
        <img src={url} alt={name} className={styles.image} onError={() => setLoadFailed(true)} />
      </div>
    );
  } else if (type === "pdf") {
    body = <iframe src={url} title={name} className={styles.frame} />;
  } else if (type === "csv" || type === "text") {
    if (textState.loading) {
      body = (
        <div className={styles.message} role="status">
          <p className={styles.messageHint}>{t("chat.preview.loading", "Loading preview...")}</p>
        </div>
      );
    } else if (textState.error || textState.content == null) {
      body = message(
        t("chat.preview.loadFailed", "This file couldn't be loaded for preview. You can download it instead."),
      );
    } else if (type === "text") {
      body = <pre className={styles.text}>{textState.content}</pre>;
    } else {
      const rows = parseCsv(textState.content);
      const [header = [], ...dataRows] = rows;
      const shown = dataRows.slice(0, MAX_TABLE_ROWS);
      body = (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                {header.map((cell, i) => (
                  <th key={i}>{cell}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r, ri) => (
                <tr key={ri}>
                  {header.map((_, ci) => (
                    <td key={ci}>{r[ci] ?? ""}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {dataRows.length > MAX_TABLE_ROWS && (
            <p className={styles.messageHint}>
              {t("chat.preview.rowsLimited", "Showing the first {{count}} rows. Download the file to see all of them.", {
                count: MAX_TABLE_ROWS,
              })}
            </p>
          )}
        </div>
      );
    }
  } else {
    body = message(t("chat.preview.notAvailable", "Preview isn't available for this file type. Download it to open it."));
  }

  const downloadLabel = t("chat.preview.download", "Download");

  return createPortal(
    <div
      className={styles.overlay}
      dir={isRtl ? "rtl" : "ltr"}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCloseRef.current?.();
      }}
    >
      <div role="dialog" aria-modal="true" aria-label={name} className={styles.card}>
        <div className={styles.header}>
          <FileTypeTile fileName={name} contentType={contentType} isImage={isImage} />
          <div className={styles.titleBlock}>
            <p className={styles.title} title={name}>
              {name}
            </p>
            {details && <p className={styles.details}>{details}</p>}
          </div>
          <button
            type="button"
            className={`${styles.headerBtn} ${styles.downloadBtn}`}
            onClick={handleDownload}
            disabled={downloading || !url}
            aria-label={`${downloadLabel} ${name}`}
          >
            {downloading ? (
              <span className={styles.spinner} aria-hidden="true" />
            ) : (
              <FiDownload size={16} aria-hidden="true" />
            )}
            <span className={styles.downloadLabel}>{downloadLabel}</span>
          </button>
          <button
            ref={closeRef}
            type="button"
            className={`${styles.headerBtn} ${styles.closeBtn}`}
            onClick={() => onCloseRef.current?.()}
            aria-label={t("chat.preview.close", "Close")}
            title={t("chat.preview.close", "Close")}
          >
            <RxCross2 size={20} aria-hidden="true" />
          </button>
        </div>
        {downloadFailed && (
          <p role="alert" className={styles.downloadError}>
            {t("chat.preview.downloadFailed", "Couldn't download the file. Please try again.")}
          </p>
        )}
        <div className={styles.body}>{body}</div>
      </div>
    </div>,
    document.body,
  );
};

export default AttachmentPreview;
